import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { UserPlus, X } from "lucide-react";
import {
  addPartnerMember,
  removePartnerMember,
  setPartnerMemberRole,
} from "@/lib/partnerApi";
import { API_BASE_URL } from "@/lib/apiBase";
import { getFullName } from "@/lib/userUtils";
import { toastError, toastSuccess } from "@/lib/toast";
import OrgMemberAvatar from "@/components/avatars/OrgMemberAvatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

function personName(person) {
  if (!person) return "Unknown";
  if (person.first_name || person.last_name) return getFullName(person);
  return person.display_name || "Unknown";
}

function MemberRow({ member, children }) {
  return (
    <div className="flex items-center gap-3">
      <OrgMemberAvatar
        user={member}
        className="h-8 w-8 shrink-0"
        fallbackClassName="text-xs font-semibold"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">{personName(member)}</p>
        {member.title ? (
          <p className="text-xs text-muted-foreground truncate">
            {member.title}
          </p>
        ) : null}
      </div>
      {children}
    </div>
  );
}

/**
 * Split roster for a partner channel. Co-admins manage only their own org's
 * side; the partner side is always read-only here.
 *
 * @param {{ active?: boolean }} props — when false, skips teammate fetch (e.g. closed dialog).
 */
export default function PartnerChannelMembersPanel({
  channel,
  viewerOrgId,
  channelId,
  accessToken,
  onChannelChange,
  active = true,
  className,
}) {
  const [orgMembers, setOrgMembers] = useState([]);
  const [orgMembersLoading, setOrgMembersLoading] = useState(false);
  const [orgMembersFailed, setOrgMembersFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [query, setQuery] = useState("");
  const [busyUserId, setBusyUserId] = useState(null);

  const members = channel?.members || [];
  const canManage = !!channel?.can_manage_members;
  const canAdd = !!channel?.can_add_members;
  const ourRows = members.filter((m) => m.org_id === viewerOrgId);
  const theirRows = members.filter((m) => m.org_id !== viewerOrgId);
  const ourOrgName = ourRows[0]?.org_name || "Your organization";
  const partnerName = channel?.partner_org?.name || "Partner organization";

  useEffect(() => {
    if (!active || !canAdd || !accessToken || !viewerOrgId) return undefined;
    let cancelled = false;
    setOrgMembersLoading(true);
    (async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/org/${encodeURIComponent(viewerOrgId)}/members`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        const list = Array.isArray(res.data)
          ? res.data
          : res.data?.members || [];
        if (!cancelled) {
          setOrgMembers(list);
          setOrgMembersFailed(false);
        }
      } catch {
        if (!cancelled) {
          setOrgMembers([]);
          setOrgMembersFailed(true);
        }
      } finally {
        if (!cancelled) setOrgMembersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active, canAdd, accessToken, viewerOrgId, reloadKey]);

  useEffect(() => {
    if (!active) setQuery("");
  }, [active]);

  const inChannel = useMemo(
    () => new Set(ourRows.map((m) => m.user_id)),
    [ourRows]
  );

  const available = useMemo(
    () =>
      orgMembers
        .map((m) => {
          const user = m.user || m;
          const userId = user.user_id || user.userId;
          return {
            user_id: userId,
            first_name: user.first_name ?? null,
            last_name: user.last_name ?? null,
            display_name: getFullName(user) || user.email || userId,
            title: m.title || null,
            avatar_url: m.member_avatar_url || null,
            member_avatar_url: m.member_avatar_url || null,
          };
        })
        .filter((m) => m.user_id && !inChannel.has(m.user_id)),
    [orgMembers, inChannel]
  );

  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    return available
      .filter((m) => !q || m.display_name.toLowerCase().includes(q))
      .slice(0, 8);
  }, [available, query]);

  const run = async (userId, fn, successMessage) => {
    setBusyUserId(userId);
    try {
      const res = await fn();
      if (res?.channel) onChannelChange?.(res.channel);
      if (successMessage) toastSuccess(successMessage);
    } catch (err) {
      toastError(err, "Could not update the roster");
    } finally {
      setBusyUserId(null);
    }
  };

  return (
    <div className={className || "space-y-5"}>
      <div className="space-y-2">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {ourOrgName} · {ourRows.length}
        </p>
        {ourRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No members yet.</p>
        ) : (
          ourRows.map((m) => {
            const isCoAdmin = m.role === "co_admin";
            const busy = busyUserId === m.user_id;
            return (
              <MemberRow key={m.member_id || m.user_id} member={m}>
                {canManage ? (
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-xs"
                      disabled={busy}
                      onClick={() =>
                        run(
                          m.user_id,
                          () =>
                            setPartnerMemberRole(
                              accessToken,
                              viewerOrgId,
                              channelId,
                              m.user_id,
                              isCoAdmin ? "member" : "co_admin"
                            ),
                          isCoAdmin
                            ? `${personName(m)} is now a member`
                            : `${personName(m)} is now a co-admin`
                        )
                      }
                    >
                      {isCoAdmin ? "Co-admin" : "Member"}
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      disabled={busy}
                      title={`Remove ${personName(m)}`}
                      aria-label={`Remove ${personName(m)}`}
                      onClick={() =>
                        run(
                          m.user_id,
                          () =>
                            removePartnerMember(
                              accessToken,
                              viewerOrgId,
                              channelId,
                              m.user_id
                            ),
                          `Removed ${personName(m)}`
                        )
                      }
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ) : isCoAdmin ? (
                  <Badge variant="outline" className="shrink-0 font-normal">
                    Co-admin
                  </Badge>
                ) : null}
              </MemberRow>
            );
          })
        )}
      </div>

      {canAdd ? (
        <div className="space-y-2 border-t border-border pt-4">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Add from {ourOrgName}
          </p>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search your teammates…"
            className="h-9"
          />
          {orgMembersLoading ? (
            <p className="text-sm text-muted-foreground">Loading teammates…</p>
          ) : orgMembersFailed ? (
            <div className="flex items-center gap-2">
              <p className="text-sm text-muted-foreground">
                Could not load teammates from {ourOrgName}.
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 px-2 text-xs"
                onClick={() => setReloadKey((k) => k + 1)}
              >
                Retry
              </Button>
            </div>
          ) : candidates.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {query.trim()
                ? "No matching teammates."
                : orgMembers.length === 0
                  ? `No other people found in ${ourOrgName}.`
                  : `All ${orgMembers.length} ${
                      orgMembers.length === 1 ? "person" : "people"
                    } in ${ourOrgName} are already in this channel.`}
            </p>
          ) : (
            candidates.map((c) => (
              <MemberRow key={c.user_id} member={c}>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-xs shrink-0"
                  disabled={busyUserId === c.user_id}
                  onClick={() =>
                    run(
                      c.user_id,
                      () =>
                        addPartnerMember(
                          accessToken,
                          viewerOrgId,
                          channelId,
                          c.user_id,
                          "member"
                        ),
                      `Added ${c.display_name}`
                    )
                  }
                >
                  <UserPlus className="h-3.5 w-3.5 mr-1" />
                  Add
                </Button>
              </MemberRow>
            ))
          )}
          {!orgMembersLoading &&
          !orgMembersFailed &&
          !query.trim() &&
          available.length > candidates.length ? (
            <p className="text-[11px] text-muted-foreground">
              Showing {candidates.length} of {available.length} — search to find
              someone else.
            </p>
          ) : null}
          <p className="text-[11px] text-muted-foreground">
            Anyone you add can read the whole channel history from both
            organizations.
          </p>
        </div>
      ) : null}

      <div className="space-y-2 border-t border-border pt-4">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {partnerName} · {theirRows.length}
        </p>
        {theirRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No members yet.</p>
        ) : (
          theirRows.map((m) => (
            <MemberRow key={m.member_id || m.user_id} member={m}>
              {m.role === "co_admin" ? (
                <Badge variant="outline" className="shrink-0 font-normal">
                  Co-admin
                </Badge>
              ) : null}
            </MemberRow>
          ))
        )}
      </div>
    </div>
  );
}

export function partnerMembersDescription(channel, viewerOrgId) {
  const canManage = !!channel?.can_manage_members;
  const ourOrgName =
    (channel?.members || []).find((m) => m.org_id === viewerOrgId)?.org_name ||
    "Your organization";
  const partnerName = channel?.partner_org?.name || "Partner organization";
  if (canManage) {
    return `You are a co-admin, so you manage who from ${ourOrgName} is in this channel. Only ${partnerName} can change their own side.`;
  }
  return "People from both organizations who can see this channel.";
}
